import { useCallback, useEffect, useState } from "react";
import { useAuth, useUser } from "@clerk/nextjs";
import { apiFetch } from "@/lib/api";

/** Roles mirrored from the API's ``features.py`` (single source of truth there). */
export type Role = "anonymous" | "user" | "beta" | "paid" | "admin";

/** GET /me — role + the features this caller may use. */
export interface Me {
  name: string;
  email: string;
  role: Role;
  is_admin: boolean;
  features: string[];
}

/** GET /usage — monthly LLM quota for server-billed calls. */
export interface Usage {
  role: Role;
  quota: number | null; // null = unlimited
  used_this_month: number;
  remaining: number | null; // null = unlimited
  use_byok: boolean;
}

/** Feature names exactly as registered in the API's ``FEATURES`` matrix. */
export const FEATURES = {
  caseSources: "case.sources",
  caseDocument: "case.document",
  caseSummary: "case.summary",
  accountKeys: "account.keys",
  accountLlm: "account.llm",
} as const;

/**
 * Server-side access state for the UI: role + allowed features (``/me``,
 * works for anonymous visitors too) and the monthly LLM quota (``/usage``,
 * signed-in only). Refetches when the signed-in state changes, so the UI
 * gates itself from the same matrix that enforces it server-side.
 */
export function useAccess() {
  const { getToken } = useAuth();
  const { isSignedIn } = useUser();
  const [me, setMe] = useState<Me | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);

  const refreshUsage = useCallback(async () => {
    try {
      const res = await apiFetch("/usage", { token: (await getToken()) ?? undefined });
      if (res.ok) setUsage((await res.json()) as Usage);
    } catch {
        /* quota display is non-critical; leave previous value */
      }
  }, [getToken]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isSignedIn) {
        setUsage(null);
      }
      try {
        const token = isSignedIn ? ((await getToken()) ?? undefined) : undefined;
        const res = await apiFetch("/me", { token });
        if (cancelled || !res.ok) return;
        const data = (await res.json()) as Me;
        setMe(data);
        if (data.role !== "anonymous") void refreshUsage();
      } catch {
        /* network failure: UI falls back to signed-in gating */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSignedIn, getToken, refreshUsage]);

  return {
    me,
    usage,
    /** True if the current caller's role may use *feature* (per /me). */
    has: (feature: string) => me?.features.includes(feature) ?? false,
    refreshUsage,
  };
}
